import React, {
  useRef,
  useState,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useEffect,
} from 'react';
import { Chip, Grid } from '@mui/material';

import PreDefinedCalendarCustomFilter, {
  Ref as PreDefinedCalendarCustomFilterRef,
} from './PreDefinedCalendarCustomFilter';

import {
  CalendarFilterOption,
  PreDefinedGranularityFilterOption,
} from 'types/types';
import { Ref as DateRangePickerRef } from './DateRangePicker';
import { Ref as DateRangeCalendarRef } from './DateRangeCalendar';
import { Ref as DataGranularitiesRef } from './DataGranularities';

export interface Ref {
  selectedOption?: CalendarFilterOption;
  handleShowCustomOption: (show: boolean) => void | undefined;
  handleChangeOptions: (
    granularityFilterOption: PreDefinedGranularityFilterOption | undefined
  ) => void;
  preDefinedCalendarCustomFilterRef: RefObject<PreDefinedCalendarCustomFilterRef>;
  handleSelectedOptions: (
    selectedOption: CalendarFilterOption | undefined
  ) => void;
}

interface Props {
  selectedFilterOption?: string;
  customFilteredEnteredValues?: {
    daysFrom: number;
    daysTo: number;
  };
  calendarFilterOptions: {
    [key: string]: CalendarFilterOption[];
  };
  dateRangePickerRef: RefObject<DateRangePickerRef>;
  dateRangeCalendarRef: RefObject<DateRangeCalendarRef>;
  dataGranularitiesRef: RefObject<DataGranularitiesRef>;
}

const PreDefinedCalendarFilters: ForwardRefRenderFunction<Ref, Props> = (
  {
    dateRangePickerRef,
    dateRangeCalendarRef,
    dataGranularitiesRef,
    selectedFilterOption,
    calendarFilterOptions,
    customFilteredEnteredValues,
  },
  ref
) => {
  const preDefinedCalendarCustomFilterRef =
    useRef<PreDefinedCalendarCustomFilterRef>(null);

  const [options, setOptions] = useState<CalendarFilterOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<CalendarFilterOption>();
  const [hideCustomFilter, setHideCustomFilter] = useState<boolean>(false);

  const handleSelectedOptions = (
    selectedOption: CalendarFilterOption | undefined
  ) => setSelectedOption(selectedOption);

  const filterHiddenOptions = (
    granularity: PreDefinedGranularityFilterOption
  ) => {
    const granularityHiddenOptions =
      granularity.hiddenFilterOptions.map((el) => el.toLowerCase()) ?? [];

    const filteredOptions = calendarFilterOptions[
      granularity.value.toUpperCase()
    ]?.filter(
      (el) => !granularityHiddenOptions.includes(el.value.toLowerCase())
    );

    return filteredOptions;
  };

  const handleHideCustomFilter = () => {
    const custom = 'custom';

    const granularityHiddenOptions =
      dataGranularitiesRef.current?.selectedGranularity?.hiddenFilterOptions.map(
        (el) => el.toLowerCase()
      ) ?? [];

    return granularityHiddenOptions.includes(custom);
  };

  const handleChangeOptions = (
    granularityFilterOption: PreDefinedGranularityFilterOption | undefined
  ) => {
    if (granularityFilterOption) {
      const filteredOptions = filterHiddenOptions(granularityFilterOption);

      const hideCustomFilter = handleHideCustomFilter();

      setHideCustomFilter(hideCustomFilter);
      setOptions(filteredOptions);
    }
  };

  const handleCalendarDatesChange = (option: CalendarFilterOption) => {
    const dates = option.getDates();
    dateRangePickerRef.current?.handleChangeFromAndToDates(
      dates.dateFrom,
      dates.dateTo
    );
    dateRangeCalendarRef.current?.handleChangeFromAndToDates(
      dates.dateFrom,
      dates.dateTo
    );
  };

  const handleClickOption = (option: CalendarFilterOption) => {
    setSelectedOption(option);
    handleCalendarDatesChange(option);
    preDefinedCalendarCustomFilterRef.current?.handleShowCustomOptions(false);
  };

  const handleShowCustomOption = (show: boolean) =>
    preDefinedCalendarCustomFilterRef.current?.handleShowCustomOptions(show);

  useImperativeHandle(ref, () => ({
    selectedOption,
    handleChangeOptions,
    handleSelectedOptions,
    handleShowCustomOption,
    preDefinedCalendarCustomFilterRef,
  }));

  useEffect(() => {
    if (selectedFilterOption) {
      const foundElement = options?.find(
        (el) => el.value === selectedFilterOption
      );
      if (foundElement) {
        handleCalendarDatesChange(foundElement);
      }
      setSelectedOption(foundElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedFilterOption]);

  useEffect(() => {
    if (
      customFilteredEnteredValues?.daysFrom != undefined &&
      customFilteredEnteredValues?.daysTo != undefined &&
      customFilteredEnteredValues?.daysFrom > -1 &&
      customFilteredEnteredValues?.daysTo > -1
    ) {
      setHideCustomFilter(false);
      setTimeout(() => {
        handleShowCustomOption(true);
      }, 100);
    }
  }, [customFilteredEnteredValues]);

  return (
    <Grid container gap={2} sx={{ justifyContent: 'center' }}>
      {options &&
        options.map((el) => (
          <Grid item xs={3} key={el.value}>
            <Chip
              label={el.label}
              onClick={() => handleClickOption(el)}
              className="calendar-filter-option"
              sx={{
                backgroundColor:
                  selectedOption?.value == el.value ? '#ffe1c6 !important' : '',
              }}
            />
          </Grid>
        ))}
      {!hideCustomFilter && options?.length > 0 && (
        <PreDefinedCalendarCustomFilter
          setSelectedOption={setSelectedOption}
          ref={preDefinedCalendarCustomFilterRef}
          dateRangePickerRef={dateRangePickerRef}
          dateRangeCalendarRef={dateRangeCalendarRef}
          dataGranularitiesRef={dataGranularitiesRef}
          customFilteredEnteredValues={customFilteredEnteredValues}
        />
      )}
    </Grid>
  );
};

export default forwardRef<Ref, Props>(PreDefinedCalendarFilters);
